import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Divider,
  styled,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSigninMutation, useGoogleAuthMutation } from "Services/auth";
import { useLazyGetCompanyIdQuery } from "Services/company";
import { useLazyGetStatusQuery } from "Services/hubspot";
import { securityClient } from "securityClient";
import { useAppDispatch } from "State/hooks";
import { setCompany } from "State/companySlice";
import { GoogleLogin } from "@react-oauth/google";

// ---------- Styled Layout ----------

const MainContainer = styled(Box)({
  display: "flex",
  minHeight: "100vh",
  backgroundColor: "#f5f5f5",
});

const LeftSection = styled(Paper)({
  flex: 1,
  padding: "60px 80px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  boxShadow: "none",
  borderRadius: 0,
});

const StyledTextField = styled(TextField)({
  marginBottom: "20px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
  },
});

const SignInButton = styled(Button)({
  background: "linear-gradient(90deg, #2A7BFF 0%, #3F8CFF 100%)",
  color: "white",
  padding: "12px",
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "15px",
  "&:hover": {
    background: "linear-gradient(90deg, #1D63E8 0%, #2A7BFF 100%)",
  },
});


// ---------- Component ----------

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [signin, { isLoading }] = useSigninMutation();
  const [googleAuth] = useGoogleAuthMutation();
  const [fetchCompanyId] = useLazyGetCompanyIdQuery();
  const [fetchHubspotStatus] = useLazyGetStatusQuery();
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleGoogleSuccess = async (idToken: string) => {
    try {
      const result = await googleAuth({ idToken }).unwrap();
      localStorage.setItem("idToken", result.idToken);
      localStorage.setItem("accessToken", result.accessToken);
      localStorage.setItem("authProvider", "google");
      if (result.refreshToken) localStorage.setItem("refreshToken", result.refreshToken);
      securityClient.setAccessTokenSilently(async () => result.idToken);

      if (result.isNewUser) {
        navigate("/connect-hubspot");
        return;
      }

      // Seed profile cache so Settings shows correct values immediately.
      // jobTitle is not in this response — Settings will complete it on first visit.
      if (result.user?.firstName) {
        localStorage.setItem("userProfile", JSON.stringify({ firstName: result.user.firstName, lastName: result.user.lastName }));
      }

      const { data } = await fetchCompanyId();
      const companyId = data?.companyId ?? "";
      if (companyId) {
        localStorage.setItem("companyId", companyId);
        dispatch(setCompany(companyId));
        const { data: hubspotStatus } = await fetchHubspotStatus();
        if (hubspotStatus?.connected) {
          localStorage.setItem("hubspotConnected", "true");
          navigate("/home");
        } else {
          navigate("/connect-hubspot");
        }
      } else {
        navigate("/connect-hubspot");
      }
    } catch (err: any) {
      setErrorMsg(err?.data?.message || err?.message || "Google sign-in failed");
    }
  };


  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  const handleChange =
    (field: "email" | "password") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: event.target.value });
      if (fieldErrors[field]) setFieldErrors({ ...fieldErrors, [field]: undefined });
    };

  const validate = (): boolean => {
    const errs: { email?: string; password?: string } = {};
    if (!formData.email.trim()) {
      errs.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = "Enter a valid email address.";
    }
    // Emptiness check ignores whitespace, but the raw value is still what gets submitted —
    // leading/trailing spaces can be legitimate password characters.
    if (!formData.password.trim()) errs.password = "Password is required.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!validate()) return;
    try {
      const result = await signin({ email: formData.email, password: formData.password }).unwrap();
      localStorage.setItem("idToken", result.idToken);
      localStorage.setItem("accessToken", result.accessToken);
      localStorage.setItem("authProvider", "email");
      if (result.refreshToken) localStorage.setItem("refreshToken", result.refreshToken);
      securityClient.setAccessTokenSilently(async () => result.idToken);
      // Seed profile cache so Settings shows correct values immediately.
      // jobTitle is not in this response — Settings will complete it on first visit.
      if (result.user?.firstName) {
        localStorage.setItem("userProfile", JSON.stringify({ firstName: result.user.firstName, lastName: result.user.lastName }));
      }
      const companyId = result.user.companyId;
      if (companyId) {
        localStorage.setItem("companyId", companyId);
        dispatch(setCompany(companyId));
        const { data: hubspotStatus } = await fetchHubspotStatus();
        if (hubspotStatus?.connected) {
          localStorage.setItem("hubspotConnected", "true");
          navigate("/home");
        } else {
          navigate("/connect-hubspot");
        }
      } else {
        navigate("/connect-hubspot");
      }
    } catch (err: any) {
      setErrorMsg(err?.data?.message || err?.message || "Invalid email or password");
    }
  };

  return (
    <MainContainer>
      {/* LEFT SECTION */}
      <LeftSection>
        <Box maxWidth="400px" margin="0 auto">
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Welcome Back
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
            sx={{ mb: 4 }}
          >
            Sign in to access your dashboard.
          </Typography>

          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>
          )}

          <form onSubmit={handleSubmit}>
            <StyledTextField
              fullWidth
              placeholder="Email"
              value={formData.email}
              onChange={handleChange("email")}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
            />

            <StyledTextField
              fullWidth
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange("password")}
              error={!!fieldErrors.password}
              helperText={fieldErrors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box textAlign="right" mb={2}>
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => navigate("/forgot-password")}
              >
                Forgot password?
              </Link>
            </Box>

            <SignInButton fullWidth type="submit" disabled={isLoading}>
              {isLoading ? "Signing in..." : "Sign in"}
            </SignInButton>
          </form>

          <Divider sx={{ my: 3 }}>or</Divider>

          <Stack direction="row" justifyContent="center">
            <GoogleLogin
              onSuccess={(response) => handleGoogleSuccess(response.credential!)}
              onError={() => setErrorMsg("Google sign-in failed")}
              width="400px"
            />
          </Stack>

          <Typography variant="body2" textAlign="center" mt={4}>
            Don’t have an account?{" "}
            <Link
              component="button"
              underline="hover"
              fontWeight={600}
              onClick={() => navigate("/signup")}
            >
              Sign up
            </Link>
          </Typography>
        </Box>
      </LeftSection>
    </MainContainer>
  );
};

export default Login;